/**
 * Vesper Updates Service (Shared)
 *
 * Powers the Updates feed (Phase 8) — short-form founder build-logs.
 */

import apiClient from './api.client';
import { API } from '../constants/api';
import { deduped, bustByPrefix } from './dedupedFetch';

export type UpdatePostType = 'building' | 'win' | 'learning' | 'ask' | 'looking_for';
export type UpdatePosterTone = 'gold' | 'violet' | 'teal' | 'rose' | 'amber' | 'cyan';
export type UpdateStageTag = 'pre_seed' | 'seed' | 'series_a' | 'growth';

export interface UpdateRecord {
  id: string;
  authorId: string;
  streamUid: string | null;
  videoUrl: string | null;
  thumbnailUrl: string | null;
  durationSec: number | null;
  postType: UpdatePostType;
  caption: string;
  sectorTags: string[];
  stageTag: UpdateStageTag | null;
  topicTags: string[];
  lookingForTag: string | null;
  networkOnly: boolean;
  posterTone: UpdatePosterTone;
  viewsCount: number;
  savesCount: number;
  vouchesCount: number;
  dmsCount: number;
  createdAt: string;
}

export interface UpdateFeedItem extends UpdateRecord {
  authorDisplayName: string;
  authorAvatarUrl: string | null;
  authorCity: string | null;
  authorBio: string | null;
  authorRole: string | null;
  viewerHasSaved: boolean;
  viewerHasVouched: boolean;
}

export interface CreateUpdatePayload {
  postType: UpdatePostType;
  caption: string;
  sectorTags?: string[];
  stageTag?: UpdateStageTag | null;
  topicTags?: string[];
  lookingForTag?: string | null;
  networkOnly?: boolean;
  posterTone?: UpdatePosterTone;
  /** Cloudflare Stream identifiers — populated when video upload completes */
  streamUid?: string | null;
  videoUrl?: string | null;
  thumbnailUrl?: string | null;
  durationSec?: number | null;
}

// ── API ──────────────────────────────────────────────────────────────────────

export async function getFeed(limit = 20, offset = 0): Promise<UpdateFeedItem[]> {
  return deduped(`updates:feed:limit=${limit}:offset=${offset}`, async () => {
    const res = await apiClient.get<{ data: { items: UpdateFeedItem[] } }>(
      `${API.UPDATES_FEED}?limit=${limit}&offset=${offset}`,
    );
    return res.data.data.items;
  });
}

export async function getByAuthor(authorId: string, limit = 6): Promise<UpdateRecord[]> {
  return deduped(`updates:byAuthor:${authorId}:limit=${limit}`, async () => {
    const res = await apiClient.get<{ data: { items: UpdateRecord[] } }>(
      `${API.UPDATES_BY_AUTHOR(authorId)}?limit=${limit}`,
    );
    return res.data.data.items;
  });
}

/**
 * Returns the viewer's saved Updates, most-recently-saved first. Powers
 * the Saved view linked from /you. Unwraps to UpdateFeedItem so the
 * existing UpdateCard renders unchanged. Cache key is paginated; the
 * `me:saved:` prefix is busted by save/unsave so a freshly-saved post
 * shows up at the top immediately.
 */
export async function getSaved(limit = 20, offset = 0): Promise<UpdateFeedItem[]> {
  return deduped(`me:saved:limit=${limit}:offset=${offset}`, async () => {
    const res = await apiClient.get<{ data: { items: UpdateFeedItem[] } }>(
      `${API.UPDATES_SAVED}?limit=${limit}&offset=${offset}`,
    );
    return res.data.data.items;
  });
}

/** Single Update with viewer-state hydration. Used by the deep-link path
 *  on the Updates feed (`?focus=<id>`). Returns null on 404. */
export async function getById(updateId: string): Promise<UpdateFeedItem | null> {
  try {
    const res = await apiClient.get<{ data: { item: UpdateFeedItem } }>(API.UPDATES_BY_ID(updateId));
    return res.data.data.item;
  } catch {
    return null;
  }
}

export async function createUpdate(payload: CreateUpdatePayload): Promise<UpdateRecord> {
  const res = await apiClient.post<{ data: UpdateRecord }>(API.UPDATES_CREATE, payload);
  // New post must appear in every feed page + on the author's profile strip.
  bustByPrefix('updates:feed:');
  bustByPrefix('updates:byAuthor:');
  return res.data.data;
}

/**
 * Proxy a recorded/library video through the backend to Cloudflare Stream.
 * Returns the streamUid; the caller passes it to {@link createUpdate} along
 * with form metadata. The backend derives playback + thumbnail URLs.
 */
export async function uploadVideo(
  blob: Blob,
  filename = 'update.webm',
): Promise<{ streamUid: string }> {
  const fd = new FormData();
  fd.append('file', blob, filename);
  const res = await apiClient.post<{ data: { streamUid: string } }>(
    API.UPDATES_UPLOAD,
    fd,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return res.data.data;
}

export async function saveUpdate(updateId: string): Promise<void> {
  await apiClient.post(API.UPDATES_SAVE(updateId));
  // The Saved list ordering is save-time DESC, so a fresh save belongs at
  // the top of every paginated key. Bust everything in `me:saved:`.
  bustByPrefix('me:saved:');
}

export async function unsaveUpdate(updateId: string): Promise<void> {
  await apiClient.delete(API.UPDATES_SAVE(updateId));
  bustByPrefix('me:saved:');
}

export async function vouchUpdate(updateId: string): Promise<void> {
  await apiClient.post(API.UPDATES_VOUCH(updateId));
}

export async function unvouchUpdate(updateId: string): Promise<void> {
  await apiClient.delete(API.UPDATES_VOUCH(updateId));
}

/**
 * Track a view (Sprint 3). Best-effort — fire-and-forget on the client.
 * No dedupe yet; the backend just increments views_count.
 */
export async function trackView(updateId: string): Promise<void> {
  try {
    await apiClient.post(API.UPDATES_VIEW(updateId));
  } catch {
    /* silent — view tracking shouldn't block UX */
  }
}

export async function deleteUpdate(updateId: string): Promise<void> {
  await apiClient.delete(API.UPDATE_DELETE(updateId));
  bustByPrefix('updates:feed:');
  bustByPrefix('updates:byAuthor:');
}

/**
 * Hide an Update from the author's profile strip without removing it from
 * feeds it has already surfaced in. A12 of YC demo build.
 */
export async function hideUpdate(updateId: string): Promise<void> {
  await apiClient.post(API.UPDATE_HIDE(updateId));
  bustByPrefix('updates:byAuthor:');
}
